/**
 * Определение «узкого» экрана для мобильной версии (план 110).
 *
 * ОДИН порог на всю CRM — `lg` Tailwind (1024px). Не три и не пять: каждая
 * дополнительная граница даёт комбинации, которые никто не проверяет руками, а
 * чек-лист прогона растёт кратно. Всё, что шире, — прежний десктоп до пикселя.
 *
 * CSS-ветвления делаются классами `max-lg:` прямо в разметке; этот хук нужен
 * там, где от ширины зависит СТРУКТУРА (какой из двух экранов рендерить в
 * мессенджере, какой размер отдать модалке), а не оформление.
 *
 * Значение считается синхронно при первом рендере: `useState(() => …)` вместо
 * «false, а потом эффект» — иначе на телефоне первый кадр рисуется десктопным
 * и экран заметно дёргается.
 */
import { useEffect, useState } from 'react';

/** Совпадает с `lg` из Tailwind: всё ниже — телефон/узкий планшет. */
export const MOBILE_QUERY = '(max-width: 1023.98px)';

function matches(query: string): boolean {
  // SSR/тестовый рантайм без matchMedia — считаем десктопом: он и был всегда.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

/** Подписка на media-query. `true` — экран уже, чем `lg`. */
export function useMediaQuery(query: string): boolean {
  const [value, setValue] = useState(() => matches(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent): void => setValue(e.matches);
    // Значение могло измениться между первым рендером и подпиской (поворот
    // экрана на старте) — синхронизируем.
    setValue(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return value;
}

/** Узкий экран: телефон или планшет в портрете. */
export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_QUERY);
}
